import React, { useEffect, useState } from 'react';
import { Table, Button, Input, Modal, message, Popconfirm, Tag, Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { EditOutlined, DeleteOutlined, UndoOutlined, PictureOutlined } from '@ant-design/icons';
import { WordPressAPI } from '../../services/api/api';
import { WCProduct } from '../../types/wordpress';
import ProductForm from './ProductForm';
import type { ColumnsType } from 'antd/es/table';

const ProductList: React.FC = () => {
    const [products, setProducts] = useState<WCProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState<WCProduct | null>(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [form] = Form.useForm();

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const data = await WordPressAPI.getProducts({
                page: currentPage,
                per_page: pageSize,
                search: searchText
            });
            setProducts(data.items);
            setTotal(data.total);
        } catch (error) {
            message.error('Failed to fetch products');
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [currentPage, pageSize, searchText]);

    const handleEdit = (record: WCProduct) => {
        setEditingProduct(record);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingProduct(null);
        setIsModalVisible(true);
    };

    const columns: ColumnsType<WCProduct> = [
        {
            title: 'Featured Image',
            dataIndex: 'images',
            key: 'featured_image',
            width: 80,
            render: (images: any[], record: WCProduct) => (
                <div style={{ width: 50, height: 50 }}>
                    {images && images.length > 0 ? (
                        <img
                            src={images[0].src}
                            alt={record.name}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px'
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                background: '#f5f5f5',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <PictureOutlined style={{ color: '#999' }} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            key: 'barcode',
        },
        {
            title: 'HTS Code',
            dataIndex: 'hts_code',
            key: 'hts_code',
        },
        {
            title: 'Unit/Box',
            dataIndex: 'unit_per_boxkg',
            key: 'unit_per_boxkg',
        },
        {
            title: 'Price',
            key: 'price',
            render: (_, record) => (
                <div>
                    <div>Current: ${Number(record.price || 0).toFixed(2)}</div>
                    <div className="text-gray-500 text-sm">
                        Last: ${Number(record.last_price || 0).toFixed(2)}
                    </div>
                </div>
            ),
            sorter: (a, b) => Number(a.price || 0) - Number(b.price || 0),
        },
        {
            title: 'Categories',
            dataIndex: 'categories',
            key: 'categories',
            render: (categories) => Array.isArray(categories)
                ? categories.map(cat => cat.name).join(', ')
                : ''
        },
        {
            title: 'Status',
            key: 'is_deleted',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            onFilter: (value, record) => {
                if (value === 'active') {
                    return !record.is_deleted;
                }
                return record.is_deleted;
            },
            render: (_, record) => (
                record.is_deleted ?
                    <Tag color="red">Inactive</Tag> :
                    <Tag color="green">Active</Tag>
            ),
        },

        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="action-buttons">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this product?"
                            onConfirm={() => handleRestore(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                className="restore-button"    >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this Product?"
                            onConfirm={() => handleSoftDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="delete-button"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingProduct(null);
    };

    const handleFormSubmit = async (values: Partial<WCProduct>) => {
        try {
            if (editingProduct) {
                await WordPressAPI.updateProduct(editingProduct.id, values);
                message.success('Product updated successfully');
            } else {
                await WordPressAPI.createProduct(values);
                message.success('Product created successfully');
            }
            handleModalClose();
            fetchProducts();
        } catch (error) {
            message.error('Failed to save product');
            console.error('Error saving product:', error);
        }
    };

    const handleSoftDelete = async (id: number) => {
        try {
            await WordPressAPI.updateProduct(id, { is_deleted: true });
            message.success('Product deactivated successfully');
            fetchProducts();
        } catch (error) {
            message.error('Failed to deactivate Product');
            console.error(error);
        }
    };

    const handleRestore = async (id: number) => {
        try {
            await WordPressAPI.updateProduct(id, { is_deleted: false });
            message.success('Product restored successfully');
            fetchProducts();
        } catch (error) {
            message.error('Failed to restore Product');
            console.error(error);
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Products</h2>
                <div className="flex gap-4">
                    <Input
                        placeholder="Search products..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-72"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                    >
                        Add New Product
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={products}
                loading={loading}
                rowKey="id"
                rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                pagination={{
                    total: total,
                    current: currentPage,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />

            <Modal
                title={editingProduct ? 'Edit Product' : 'Add New Product'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                destroyOnClose={true}
            >
                <ProductForm
                    form={form}
                    key={editingProduct?.id || 'new'}
                    product={editingProduct}
                    onCancel={handleModalClose}
                    onSubmit={handleFormSubmit}
                />
            </Modal>
        </div>
    );
};

export default ProductList; 
